import { useEffect, useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Play, RotateCcw, XCircle } from 'lucide-react';
import { SiteLayout } from '@/components/SiteLayout';
import { CodeEditor } from '@/components/CodeEditor';
import { InlineCode } from '@/components/InlineCode';
import { Button } from '@/components/ui/button';
import { findProblem, starterCode, type Problem } from '@/data/problems';
import { progressKey, useProgress } from '@/hooks/useProgress';
import { compile, deepEqual, formatCall, formatValue, type CallResult } from '@/lib/runner';
import { cn } from '@/lib/utils';
import NotFound from './NotFound';

interface TestRow {
  call: string;
  expected: unknown;
  result: CallResult;
  pass: boolean;
}

type RunState =
  | { status: 'idle' }
  | { status: 'error'; error: string }
  | { status: 'done'; rows: TestRow[]; passed: number };

function useExpected(problem: Problem | undefined) {
  return useMemo(() => {
    if (!problem) return [];
    const compiled = compile(problem.solution, problem.fn);
    if (!compiled.ok) {
      console.error(`Reference solution for ${problem.id} failed:`, compiled.error);
      return [];
    }
    return problem.tests.map((args) => {
      const r = compiled.call(args);
      return r.ok ? r.value : undefined;
    });
  }, [problem]);
}

export default function ProblemPage() {
  const { categoryId = '', problemId = '' } = useParams();
  const ref = findProblem(categoryId, problemId);

  if (!ref) return <NotFound />;
  // Remount on problem change so editor state resets cleanly.
  return <ProblemView key={`${categoryId}/${problemId}`} categoryId={categoryId} problemId={problemId} />;
}

function ProblemView({ categoryId, problemId }: { categoryId: string; problemId: string }) {
  const ref = findProblem(categoryId, problemId)!;
  const { problem, category, index } = ref;
  const key = progressKey(category.id, problem.id);
  const { progress, update } = useProgress();
  const saved = progress[key];

  const [code, setCode] = useState(() => saved?.code ?? starterCode(problem));
  const [run, setRun] = useState<RunState>({ status: 'idle' });
  const [showSolution, setShowSolution] = useState(false);
  const expected = useExpected(problem);

  useSeoMeta({
    title: `${category.name} › ${problem.fn} — JSBat`,
    description: problem.description.replace(/`/g, ''),
  });

  // Persist code as the user types (debounced).
  useEffect(() => {
    const t = setTimeout(() => update(key, { code }), 400);
    return () => clearTimeout(t);
  }, [code, key, update]);

  const prev = index > 0 ? category.problems[index - 1] : undefined;
  const next = index < category.problems.length - 1 ? category.problems[index + 1] : undefined;
  const canSeeSolution = category.id === 'Warmup-1' || saved?.attempted || saved?.solved;

  const handleRun = () => {
    const compiled = compile(code, problem.fn);
    if (!compiled.ok) {
      setRun({ status: 'error', error: compiled.error });
      update(key, { code, attempted: true });
      return;
    }
    const rows: TestRow[] = problem.tests.map((args, i) => {
      const result = compiled.call(args);
      const exp = expected[i];
      return {
        call: formatCall(problem.fn, args),
        expected: exp,
        result,
        pass: result.ok && deepEqual(result.value, exp),
      };
    });
    const passed = rows.filter((r) => r.pass).length;
    setRun({ status: 'done', rows, passed });
    const allPass = passed === rows.length;
    update(key, { code, attempted: true, ...(allPass ? { solved: true } : {}) });
  };

  const handleReset = () => {
    setCode(starterCode(problem));
    setRun({ status: 'idle' });
  };

  const allPassed = run.status === 'done' && run.passed === run.rows.length;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 font-mono text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span aria-hidden>/</span>
            <Link to={`/problems/${category.id}`} className="hover:text-foreground">{category.name}</Link>
            <span aria-hidden>/</span>
            <span className="text-foreground">{problem.fn}</span>
            {saved?.solved && <CheckCircle2 className="size-4 text-success" aria-label="Solved" />}
          </div>
          <div className="flex gap-2">
            <Button asChild={!!prev} variant="outline" size="sm" disabled={!prev}>
              {prev ? (
                <Link to={`/problems/${category.id}/${prev.id}`}><ArrowLeft /> Prev</Link>
              ) : (
                <span><ArrowLeft /> Prev</span>
              )}
            </Button>
            <Button asChild={!!next} variant="outline" size="sm" disabled={!next}>
              {next ? (
                <Link to={`/problems/${category.id}/${next.id}`}>Next <ArrowRight /></Link>
              ) : (
                <span>Next <ArrowRight /></span>
              )}
            </Button>
          </div>
        </nav>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Left: prompt + editor */}
          <section className="min-w-0 space-y-5">
            <div>
              <h1 className="font-mono text-3xl font-extrabold tracking-tight sm:text-4xl">{problem.fn}</h1>
              <p className="mt-4 text-lg leading-relaxed">
                <InlineCode text={problem.description} />
              </p>
            </div>

            <div className="rounded-lg border bg-muted/40 p-4 font-mono text-sm">
              {problem.tests.slice(0, 3).map((args, i) => (
                <div key={i} className="truncate">
                  {formatCall(problem.fn, args)} <span className="text-muted-foreground">→</span>{' '}
                  {expected.length ? formatValue(expected[i]) : '…'}
                </div>
              ))}
            </div>

            <CodeEditor
              value={code}
              onChange={setCode}
              onRun={handleRun}
              className="h-80"
              ariaLabel={`Code for ${problem.fn}`}
            />

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleRun} size="lg" className="bg-brand text-brand-foreground hover:bg-brand/90">
                <Play /> Go
              </Button>
              <Button onClick={handleReset} variant="outline" size="lg">
                <RotateCcw /> Reset
              </Button>
              <span className="hidden text-sm text-muted-foreground sm:inline">
                <kbd className="rounded border bg-muted px-1.5 font-mono text-xs">Ctrl</kbd>+
                <kbd className="rounded border bg-muted px-1.5 font-mono text-xs">Enter</kbd> to run
              </span>
              <Button
                onClick={() => setShowSolution((s) => !s)}
                variant="ghost"
                size="lg"
                className="ml-auto"
                disabled={!canSeeSolution}
                title={canSeeSolution ? undefined : 'Make an attempt first to unlock the solution'}
              >
                {showSolution ? <EyeOff /> : <Eye />} {showSolution ? 'Hide' : 'Show'} solution
              </Button>
            </div>

            {showSolution && canSeeSolution && (
              <pre className="overflow-x-auto rounded-lg border bg-code-bg p-4 font-mono text-[15px] leading-6 text-code-fg">
                {problem.solution}
              </pre>
            )}
          </section>

          {/* Right: results */}
          <section aria-live="polite" className="min-w-0">
            {run.status === 'idle' && (
              <div className="flex h-full min-h-60 items-center justify-center rounded-xl border border-dashed p-8 text-center">
                <p className="max-w-xs text-muted-foreground">
                  Write your function, then press <span className="font-semibold text-foreground">Go</span> to run it
                  against {problem.tests.length} tests.
                </p>
              </div>
            )}

            {run.status === 'error' && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-5">
                <div className="flex items-center gap-2 font-semibold text-destructive dark:text-red-300">
                  <XCircle className="size-5" /> Error
                </div>
                <pre className="mt-3 whitespace-pre-wrap font-mono text-sm">{run.error}</pre>
              </div>
            )}

            {run.status === 'done' && (
              <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
                <div
                  className={cn(
                    'flex items-center justify-between gap-3 px-4 py-3 font-semibold',
                    allPassed ? 'bg-success/15 text-success' : 'bg-destructive/10 text-destructive dark:text-red-300',
                  )}
                >
                  <span className="flex items-center gap-2">
                    {allPassed ? <CheckCircle2 className="size-5" /> : <XCircle className="size-5" />}
                    {allPassed ? 'All Correct' : `${run.passed} / ${run.rows.length} passing`}
                  </span>
                  {allPassed && next && (
                    <Button asChild size="sm" className="bg-success text-white hover:bg-success/90">
                      <Link to={`/problems/${category.id}/${next.id}`}>Next problem <ArrowRight /></Link>
                    </Button>
                  )}
                </div>
                <div className="max-h-[36rem] overflow-auto">
                  <table className="w-full text-left font-mono text-sm">
                    <thead className="sticky top-0 bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 font-medium">Expected</th>
                        <th className="px-4 py-2 font-medium">Run</th>
                        <th className="px-4 py-2 text-right font-medium"><span className="sr-only">Status</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.rows.map((row, i) => (
                        <tr key={i} className="border-t align-top">
                          <td className="px-4 py-2">
                            <div className="break-all">{row.call} <span className="text-muted-foreground">→</span> {formatValue(row.expected)}</div>
                          </td>
                          <td className={cn('px-4 py-2 break-all', !row.pass && 'text-destructive dark:text-red-300')}>
                            {row.result.ok ? formatValue(row.result.value) : row.result.error}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span
                              className={cn(
                                'inline-block rounded px-2 py-0.5 text-xs font-bold',
                                row.pass ? 'bg-success/20 text-success' : 'bg-destructive/15 text-destructive dark:text-red-300',
                              )}
                            >
                              {row.pass ? 'OK' : 'X'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}
